/**
 * fsStore.js — persistenza locale (IndexedDB) per la Forward Secrecy.
 * ⚠️ Materiale SENSIBILE: chiavi private prekey + stato del ratchet per
 * conversazione. Resta solo su questo dispositivo (DNA no-sync di Nullum).
 *
 * IndexedDB serializza nativamente Uint8Array e Map (structured clone), quindi
 * lo stato del ratchet ({DHs,DHr,RK,CKs,CKr,Ns,Nr,PN,skipped:Map}) si salva e
 * ricarica così com'è, senza serializzazione manuale.
 *
 *   sessions  : convKey (sigil peer minuscolo) -> stato ratchet
 *   prekeys   : 'self' -> { spks:[{id,pub,priv}], opks:[{id,pub,priv}] }
 *
 * Ogni get/put è una transazione a sé (niente await tra due richieste nella
 * stessa tx → evita TransactionInactiveError). Il read-modify-write delle OPK
 * non è atomico, ma su singolo dispositivo (JS single-thread, open-sessione
 * serializzata per conversazione) va bene.
 */

const DB_NAME = 'nullum-fs';
const DB_VER = 1;
const ST_SESS = 'sessions';
const ST_PRE = 'prekeys';
const SELF = 'self';
const MAX_SPKS = 3; // tieni le ultime N SPK per le sessioni in volo durante una rotazione
const MAX_OPKS_LOCAL = 300; // tetto ai segreti OPK conservati (il server ne tiene max 200)

// ── Multi-identità: lo stato FS (sessioni ratchet + prekey) è per-IDENTITA'.
// Le chiavi IDB sono prefissate col sigil attivo, così switchare account NON
// mischia/corrompe i ratchet. setFsIdentity() va chiamata a ogni sblocco/switch.
let _fsId = '';
export function setFsIdentity(sigilLower) {
  _fsId = String(sigilLower || '').toLowerCase().replace(/^@/, '');
}
export function getFsIdentity() { return _fsId; }
const nk = (key) => (_fsId ? `${_fsId}::${key}` : key);

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ST_SESS)) db.createObjectStore(ST_SESS);
      if (!db.objectStoreNames.contains(ST_PRE)) db.createObjectStore(ST_PRE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(store, key) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const r = db.transaction(store, 'readonly').objectStore(store).get(key);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function idbPut(store, key, val) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const t = db.transaction(store, 'readwrite');
    t.objectStore(store).put(val, key);
    t.oncomplete = () => res(true);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error);
  });
}
async function idbDel(store, key) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const t = db.transaction(store, 'readwrite');
    t.objectStore(store).delete(key);
    t.oncomplete = () => res(true);
    t.onerror = () => rej(t.error);
  });
}
async function idbClear(store) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const t = db.transaction(store, 'readwrite');
    t.objectStore(store).clear();
    t.oncomplete = () => res(true);
    t.onerror = () => rej(t.error);
  });
}

// ---- Sessioni ratchet ----------------------------------------------------

export const saveSession = (convKey, session) => idbPut(ST_SESS, nk(convKey), session);
export const loadSession = (convKey) => idbGet(ST_SESS, nk(convKey));
export const deleteSession = (convKey) => idbDel(ST_SESS, nk(convKey));
export const hasSession = async (convKey) => !!(await loadSession(convKey));

// ---- Prekey private del dispositivo --------------------------------------

/** Salva/ruota le prekey. La nuova SPK si antepone (tiene le ultime MAX_SPKS),
 *  le OPK si sostituiscono col nuovo lotto. */
export async function saveMyPrekeys({ spk, opks, pq }) {
  const cur = (await idbGet(ST_PRE, nk(SELF))) || { spks: [], opks: [], pqs: [] };
  const spks = [spk, ...cur.spks.filter((k) => k.id !== spk.id)].slice(0, MAX_SPKS);
  // OPK: ACCUMULA (non sostituire). Il server accumula le OPK di tutti i lotti
  // pubblicati e ne consegna una qualsiasi; il client deve conservare il segreto
  // di OGNI OPK pubblicata e non ancora consumata, altrimenti un destinatario
  // non riesce a fare X3DH se gli arriva una OPK di un lotto precedente.
  const fresh = opks || [];
  const freshIds = new Set(fresh.map((o) => o.id));
  const opksMerged = [...fresh, ...(cur.opks || []).filter((o) => !freshIds.has(o.id))].slice(0, MAX_OPKS_LOCAL);
  // La chiave KEM post-quantum è pubblicata insieme alla SPK → indicizzata per spk.id.
  const pqs = pq
    ? [{ id: spk.id, pub: pq.pub, secret: pq.secret }, ...(cur.pqs || []).filter((k) => k.id !== spk.id)].slice(0, MAX_SPKS)
    : (cur.pqs || []);
  return idbPut(ST_PRE, nk(SELF), { spks, opks: opksMerged, pqs });
}
export async function loadMySpk(spkId) {
  const cur = await idbGet(ST_PRE, nk(SELF));
  return cur ? cur.spks.find((k) => k.id === spkId) || null : null;
}
/** Segreto KEM post-quantum pubblicato con la SPK `spkId` (per decapsulare). */
export async function loadMyPq(spkId) {
  const cur = await idbGet(ST_PRE, nk(SELF));
  return cur && cur.pqs ? cur.pqs.find((k) => k.id === spkId) || null : null;
}
/** Legge una OPK per id SENZA rimuoverla (consumo dopo decifratura riuscita). */
export async function peekMyOpk(opkId) {
  if (!opkId) return null;
  const cur = await idbGet(ST_PRE, nk(SELF));
  return cur ? cur.opks.find((k) => k.id === opkId) || null : null;
}
/** Consuma (rimuove) una OPK per id; null se assente/già usata. */
export async function consumeMyOpk(opkId) {
  if (!opkId) return null;
  const cur = await idbGet(ST_PRE, nk(SELF));
  if (!cur) return null;
  const i = cur.opks.findIndex((k) => k.id === opkId);
  if (i < 0) return null;
  const [used] = cur.opks.splice(i, 1);
  await idbPut(ST_PRE, nk(SELF), cur);
  return used;
}
export async function myOpkCount() {
  const cur = await idbGet(ST_PRE, nk(SELF));
  return cur ? cur.opks.length : 0;
}

export async function wipeFsStore() {
  await idbClear(ST_SESS);
  await idbClear(ST_PRE);
}
